/**
 * Default form fields configuration by assistant type
 * Defines which form fields should be pre-selected when creating a new assistant of a specific type
 *
 * Values in the arrays are checked against both the field's associatedVariable and its title.
 * A field is selected if either its associatedVariable or its title appears in the array.
 * Use associatedVariable strings (e.g. "{MONDAY}") for stable matching, or title strings as a fallback.
 */

export interface DefaultFormFieldsConfig {
    assistantType: string;
    defaultFieldTitles: string[];
}

/**
 * Default form fields for Dental Scheduling assistant type
 * These fields will be automatically selected when creating a new Dental Scheduling assistant
 */
export const DENTAL_SCHEDULING_DEFAULT_FIELDS: string[] = [
    "Office Name",
    "Office Phone Number",
    "Emergency Phone Number",
    "Office Website",
    "Office Google Maps Link",
    "Transfer Phone Number",
    "Emergency Transfer Number",
    "To Email Addresses",
    "Monday Hours",
    "Tuesday Hours",
    "Wednesday Hours",
    "Thursday Hours",
    "Friday Hours",
    "Saturday Hours",
    "Sunday Hours",
    "Closed Days",
    "About the Practice",
    "Directions",
    "Directions Text Message",
    "Default Office ID",
    "{Webhook-Application-Id}",
    "{Webhook-Application-Secret}"
];

/**
 * Default form fields for AI Answering assistant type
 * Currently empty - can be populated as needed
 */
export const AI_ANSWERING_DEFAULT_FIELDS: string[] = [];

/**
 * Default form fields for AI Ordering assistant type (no POS selected)
 * These fields will be automatically selected when creating a new AI Ordering assistant
 * before a POS is chosen.
 */
export const AI_ORDERING_DEFAULT_FIELDS: string[] = [
    "{LocationName}",
    "{Restaurant-Name}",
    "{Webhook-Restaurant-Name}",
    "{Webhook-Restaurant-PhoneNumber}",
    "{Webhook-Is-Test-Order}",
    "{Webhook-Restaurant-Id}",
    "{Webhook-SearchAddress-Latitude}",
    "{Webhook-SearchAddress-Longitude}",
    "{Webhook-Application-Secret}",
    "DISABLED_DAYS",
    "{TIME_ZONE}",
    "{Webhook-Time-Zone}",
    "{{session.returntostorenumber}}",
    "{transfer-with-calling-number}",
    "{LOCATION_SPECIFIC_OTHER_QUESTION_INFO}",
    "{Extra-EU-Prompt}",
    "{MONDAY}",    "Monday Hours",
    "{TUESDAY}",   "Tuesday Hours",
    "{WEDNESDAY}", "Wednesday Hours",
    "{THURSDAY}",  "Thursday Hours",
    "{FRIDAY}",    "Friday Hours",
    "{SATURDAY}",  "Saturday Hours",
    "{SUNDAY}",    "Sunday Hours",
];

/**
 * Default form fields for Speedline AI Ordering
 * These fields will be automatically selected when creating a new AI Ordering assistant with Speedline POS
 */
export const SPEEDLINE_DEFAULT_FIELDS: string[] = [
    // New fields (added per update)
    "{Webhook-Pos-Name}",
    "{LocationName}",
    "{Restaurant-Name}",
    "{Webhook-Restaurant-Name}",        // "Restaurant Name"
    "{Webhook-Restaurant-PhoneNumber}", // "Restaurant Phone Number" — not in original Speedline, added
    "{Webhook-Is-Test-Order}",          // "Is Test Order"
    "{Webhook-Restaurant-Id}",          // "Location Id"
    "{Webhook-SearchAddress-Latitude}", // "Location Latitude"
    "{Webhook-SearchAddress-Longitude}", // "Location Longitude"
    "{Webhook-Application-Secret}",     // "Application Secret"
    "DISABLED_DAYS",                    // "Disabled Days"
    "{TIME_ZONE}",                      // "Time Zone"
    "{Webhook-Time-Zone}",
    "{{session.returntostorenumber}}",  // "Transfer Target Phone Number"
    "{transfer-with-calling-number}",   // "Transfer With Calling Number"
    "{LOCATION_SPECIFIC_OTHER_QUESTION_INFO}", // "Non ordering information per location"
    "{Extra-EU-Prompt}",
    // Retained from original Speedline defaults
    "{Item-Welcome-Message-Generative-Response}", // "Example welcome message during closed hours"
    "{MONDAY}",    "Monday Hours",
    "{TUESDAY}",   "Tuesday Hours",
    "{WEDNESDAY}", "Wednesday Hours",
    "{THURSDAY}",  "Thursday Hours",
    "{FRIDAY}",    "Friday Hours",
    "{SATURDAY}",  "Saturday Hours",
    "{SUNDAY}",    "Sunday Hours",
    "{Webhook-Allow-Delivery-With-Card}",  // "Allow Delivery With Card"
    "{Webhook-Allow-Delivery-With-Cash}",  // "Allow Delivery With Cash"
    "{Webhook-Allow-Pickup-With-Cash}",    // "Allow Pickup With Cash"
    "{Webhook-Allow-Pickup-With-Card}",    // "Allow Pickup With Card"
    "{supportsTip}",                       // "Payment page Supports Tip"
    "{Webhook-Allow-Pickup}",              // "Allow Pickup Orders"
    "{Webhook-Allow-Delivery}",            // "Allow Delivery Orders"
    "{Webhook-Ask-For-Room-Number}",       // "Ask For Room Number"
];

/**
 * Get default form field titles for a given assistant type
 * @param assistantType - The type of assistant ('dental-scheduling', 'ai-answering', 'ai-ordering')
 * @returns Array of field associatedVariable/title strings that should be selected by default
 */
export const getDefaultFormFieldsByAssistantType = (assistantType: string): string[] => {
    switch (assistantType) {
        case 'dental-scheduling':
            return DENTAL_SCHEDULING_DEFAULT_FIELDS;
        case 'ai-answering':
            return AI_ANSWERING_DEFAULT_FIELDS;
        case 'ai-ordering':
            return AI_ORDERING_DEFAULT_FIELDS;
        default:
            return [];
    }
};

/**
 * Map of assistant types to their default field configurations
 */
export const DEFAULT_FORM_FIELDS_BY_TYPE: Record<string, string[]> = {
    'dental-scheduling': DENTAL_SCHEDULING_DEFAULT_FIELDS,
    'ai-answering': AI_ANSWERING_DEFAULT_FIELDS,
    'ai-ordering': AI_ORDERING_DEFAULT_FIELDS
};

/**
 * Additional fields to pre-select when the Voicify Sandbox (out-of-the-box) environment
 * is chosen for Speedline AI Ordering, on top of SPEEDLINE_DEFAULT_FIELDS.
 */
export const SPEEDLINE_SANDBOX_EXTRA_FIELDS: string[] = [
    "Special Dates & Holidays",
    "{TIME_ZONE}", "Time Zone",
];

/**
 * Default form fields for OLO AI Ordering
 * These location-specific fields must be filled in for each deployment.
 */
export const OLO_DEFAULT_FIELDS: string[] = [
    // New fields (added per update)
    "{Webhook-Pos-Name}",
    "{LocationName}",
    "{Restaurant-Name}",
    "{Webhook-Restaurant-Name}",        // "Restaurant Name"
    "{Webhook-Restaurant-PhoneNumber}", // "Restaurant Phone Number"
    "{Webhook-Is-Test-Order}",          // "Is Test Order"
    "{Webhook-Restaurant-Id}",          // "Location Id"
    "{Webhook-SearchAddress-Latitude}", // "Location Latitude"
    "{Webhook-SearchAddress-Longitude}", // "Location Longitude"
    "{Webhook-Application-Secret}",     // "Application Secret"
    "DISABLED_DAYS",                    // "Disabled Days"
    "{TIME_ZONE}",                      // "Time Zone"
    "{Webhook-Time-Zone}",
    "{{session.returntostorenumber}}",  // "Transfer Target Phone Number"
    "{transfer-with-calling-number}",   // "Transfer With Calling Number"
    "{LOCATION_SPECIFIC_OTHER_QUESTION_INFO}", // "Non ordering information per location"
    "{Extra-EU-Prompt}",
    // Retained from original OLO defaults
    "{Item-Welcome-Message-Generative-Response}", // "Example welcome message during closed hours"
    "{MONDAY}",    "Monday Hours",
    "{TUESDAY}",   "Tuesday Hours",
    "{WEDNESDAY}", "Wednesday Hours",
    "{THURSDAY}",  "Thursday Hours",
    "{FRIDAY}",    "Friday Hours",
    "{SATURDAY}",  "Saturday Hours",
    "{SUNDAY}",    "Sunday Hours",
    "{webhook-address-string-override}",           // "Address String Override"
    "{Webhook-Allow-Delivery-With-Card}",          // "Allow Delivery With Card"
    "{Webhook-Allow-Delivery-With-Cash}",          // "Allow Delivery With Cash"
    "{Webhook-Allow-Pickup-With-Cash}",            // "Allow Pickup With Cash"
    "{Webhook-Allow-Pickup-With-Card}",            // "Allow Pickup With Card"
    "{supportsTip}",                               // "Payment page Supports Tip"
    "{Webhook-Allow-Pickup}",                      // "Allow Pickup Orders"
    "{Webhook-Allow-Delivery}",                    // "Allow Delivery Orders"
    "{Webhook-Ask-For-Room-Number}",               // "Ask For Room Number"
];

/**
 * Default form fields for Adora AI Ordering
 * These location-specific fields must be filled in for each deployment.
 */
export const ADORA_DEFAULT_FIELDS: string[] = [
    // New fields (added per update)
    "{Webhook-Pos-Name}",
    "{LocationName}",
    "{Restaurant-Name}",
    "{Webhook-Restaurant-Name}",        // "Restaurant Name"
    "{Webhook-Restaurant-PhoneNumber}", // "Restaurant Phone Number"
    "{Webhook-Is-Test-Order}",          // "Is Test Order"
    "{Webhook-Restaurant-Id}",          // "Location Id"
    "{Webhook-SearchAddress-Latitude}", // "Location Latitude"
    "{Webhook-SearchAddress-Longitude}", // "Location Longitude"
    "{Webhook-Application-Secret}",     // "Application Secret"
    "DISABLED_DAYS",                    // "Disabled Days"
    "{TIME_ZONE}",                      // "Time Zone"
    "{Webhook-Time-Zone}",
    "{{session.returntostorenumber}}",  // "Transfer Target Phone Number"
    "{transfer-with-calling-number}",   // "Transfer With Calling Number"
    "{LOCATION_SPECIFIC_OTHER_QUESTION_INFO}", // "Non ordering information per location"
    "{Extra-EU-Prompt}",
    // Retained from original Adora defaults
    "{Item-Welcome-Message-Generative-Response}", // "Example welcome message during closed hours"
    "{MONDAY}",    "Monday Hours",
    "{TUESDAY}",   "Tuesday Hours",
    "{WEDNESDAY}", "Wednesday Hours",
    "{THURSDAY}",  "Thursday Hours",
    "{FRIDAY}",    "Friday Hours",
    "{SATURDAY}",  "Saturday Hours",
    "{SUNDAY}",    "Sunday Hours",
    "{webhook-address-string-override}",           // "Address String Override"
    "{Webhook-Allow-Delivery-With-Card}",          // "Allow Delivery With Card"
    "{Webhook-Allow-Delivery-With-Cash}",          // "Allow Delivery With Cash"
    "{Webhook-Allow-Pickup-With-Cash}",            // "Allow Pickup With Cash"
    "{Webhook-Allow-Pickup-With-Card}",            // "Allow Pickup With Card"
    "{supportsTip}",                               // "Payment page Supports Tip"
    "{Webhook-Allow-Pickup}",                      // "Allow Pickup Orders"
    "{Webhook-Allow-Delivery}",                    // "Allow Delivery Orders"
    "{Webhook-Ask-For-Room-Number}",               // "Ask For Room Number"
];

/**
 * Default form fields for Paytronix AI Ordering
 * These location-specific fields must be filled in for each deployment.
 */
export const PAYTRONIX_DEFAULT_FIELDS: string[] = [
    // New fields (added per update)
    "{Webhook-Pos-Name}",
    "{LocationName}",
    "{Restaurant-Name}",
    "{Webhook-Restaurant-Name}",        // "Restaurant Name"
    "{Webhook-Restaurant-PhoneNumber}", // "Restaurant Phone Number"
    "{Webhook-Is-Test-Order}",          // "Is Test Order"
    "{Webhook-Restaurant-Id}",          // "Location Id"
    "{Webhook-SearchAddress-Latitude}", // "Location Latitude"
    "{Webhook-SearchAddress-Longitude}", // "Location Longitude"
    "{Webhook-Application-Secret}",     // "Application Secret"
    "DISABLED_DAYS",                    // "Disabled Days"
    "{TIME_ZONE}",                      // "Time Zone"
    "{Webhook-Time-Zone}",
    "{{session.returntostorenumber}}",  // "Transfer Target Phone Number"
    "{transfer-with-calling-number}",   // "Transfer With Calling Number"
    "{LOCATION_SPECIFIC_OTHER_QUESTION_INFO}", // "Non ordering information per location"
    "{Extra-EU-Prompt}",
    // Retained from original Paytronix defaults
    "{Item-Welcome-Message-Generative-Response}", // "Example welcome message during closed hours"
    "{MONDAY}",    "Monday Hours",
    "{TUESDAY}",   "Tuesday Hours",
    "{WEDNESDAY}", "Wednesday Hours",
    "{THURSDAY}",  "Thursday Hours",
    "{FRIDAY}",    "Friday Hours",
    "{SATURDAY}",  "Saturday Hours",
    "{SUNDAY}",    "Sunday Hours",
    "{webhook-address-string-override}",           // "Address String Override"
    "{Webhook-Allow-Delivery-With-Card}",          // "Allow Delivery With Card"
    "{Webhook-Allow-Delivery-With-Cash}",          // "Allow Delivery With Cash"
    "{Webhook-Allow-Pickup-With-Cash}",            // "Allow Pickup With Cash"
    "{Webhook-Allow-Pickup-With-Card}",            // "Allow Pickup With Card"
    "{supportsTip}",                               // "Payment page Supports Tip"
    "{Webhook-Allow-Pickup}",                      // "Allow Pickup Orders"
    "{Webhook-Allow-Delivery}",                    // "Allow Delivery Orders"
    "{Webhook-Ask-For-Room-Number}",               // "Ask For Room Number"
];

/**
 * Default form fields for Toast AI Ordering
 * These location-specific fields must be filled in for each deployment.
 */
export const TOAST_DEFAULT_FIELDS: string[] = [
    // New fields (added per update)
    "{Webhook-Pos-Name}",
    "{LocationName}",
    "{Restaurant-Name}",
    "{Webhook-Restaurant-Name}",        // "Restaurant Name"
    "{Webhook-Restaurant-PhoneNumber}", // "Restaurant Phone Number"
    "{Webhook-Is-Test-Order}",          // "Is Test Order"
    "{Webhook-Restaurant-Id}",          // "Location Id"
    "{Webhook-SearchAddress-Latitude}", // "Location Latitude"
    "{Webhook-SearchAddress-Longitude}", // "Location Longitude"
    "{Webhook-Application-Secret}",     // "Application Secret"
    "DISABLED_DAYS",                    // "Disabled Days"
    "{TIME_ZONE}",                      // "Time Zone"
    "{Webhook-Time-Zone}",
    "{{session.returntostorenumber}}",  // "Transfer Target Phone Number"
    "{transfer-with-calling-number}",   // "Transfer With Calling Number"
    "{LOCATION_SPECIFIC_OTHER_QUESTION_INFO}", // "Non ordering information per location"
    "{Extra-EU-Prompt}",
    // Retained from original Toast defaults
    "{Item-Welcome-Message-Generative-Response}", // "Example welcome message during closed hours"
    "{MONDAY}",    "Monday Hours",
    "{TUESDAY}",   "Tuesday Hours",
    "{WEDNESDAY}", "Wednesday Hours",
    "{THURSDAY}",  "Thursday Hours",
    "{FRIDAY}",    "Friday Hours",
    "{SATURDAY}",  "Saturday Hours",
    "{SUNDAY}",    "Sunday Hours",
    "{webhook-address-string-override}",           // "Address String Override"
    "{Webhook-Allow-Delivery-With-Card}",          // "Allow Delivery With Card"
    "{Webhook-Allow-Delivery-With-Cash}",          // "Allow Delivery With Cash"
    "{Webhook-Allow-Pickup-With-Cash}",            // "Allow Pickup With Cash"
    "{Webhook-Allow-Pickup-With-Card}",            // "Allow Pickup With Card"
    "{supportsTip}",                               // "Payment page Supports Tip"
    "{Webhook-Allow-Pickup}",                      // "Allow Pickup Orders"
    "{Webhook-Allow-Delivery}",                    // "Allow Delivery Orders"
    "{Webhook-Ask-For-Room-Number}",               // "Ask For Room Number"
];

/**
 * Get default form field titles for a given POS name (AI Ordering)
 * @param posName - The POS system name ('OLO', 'SPEEDLINE', 'ADORA', 'TOAST', 'PAYTRONIX')
 * @returns Array of field associatedVariable/title strings that should be selected by default for this POS
 */
export const getDefaultFormFieldsByPosName = (posName: string): string[] => {
    switch (posName) {
        case 'OLO':
            return OLO_DEFAULT_FIELDS;
        case 'SPEEDLINE':
            return SPEEDLINE_DEFAULT_FIELDS;
        case 'ADORA':
            return ADORA_DEFAULT_FIELDS;
        case 'TOAST':
            return TOAST_DEFAULT_FIELDS;
        case 'PAYTRONIX':
            return PAYTRONIX_DEFAULT_FIELDS;
        default:
            return [];
    }
};

/**
 * Get additional field strings to pre-select when the Voicify Sandbox environment is chosen,
 * on top of the base defaults returned by getDefaultFormFieldsByPosName.
 * @param posName - The POS system name
 * @returns Array of extra field associatedVariable/title strings to also select for sandbox setup
 */
export const getSandboxExtraFormFieldsByPosName = (posName: string): string[] => {
    switch (posName) {
        case 'SPEEDLINE':
            return SPEEDLINE_SANDBOX_EXTRA_FIELDS;
        case 'OLO':
            return [];
        case 'ADORA':
            return [];
        case 'TOAST':
            return [];
        case 'PAYTRONIX':
            return [];
        default:
            return [];
    }
};
